
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Purchases.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const PURCHASE_API = `${API_BASE}/purchases`;
const SUPPLIER_API = `${API_BASE}/suppliers`;
const SUPPLIER_BANK_API = `${SUPPLIER_API}/banks`;
const PACKAGING_API = `${API_BASE}/packaging`;
const INVENTORY_API = `${API_BASE}/inventory`;
const INR_FORMATTER = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatInr = (value) => INR_FORMATTER.format(Number(value || 0));
const getItemBaseAmount = (item) => Number(item.quantity || 0) * Number(item.purchase_price || 0);
const getItemTaxAmount = (item) => (getItemBaseAmount(item) * Number(item.tax_percent || 0)) / 100;

export default function Purchases() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSupplierPaymentRoute = location.pathname === "/purchases/supplier-payment";

  const [activeTab, setActiveTab] = useState(
    location.pathname === "/suppliers" || location.pathname === "/purchases/supplier-payment"
      ? "supplier"
      : "purchases"
  );

  const [loading, setLoading] = useState(false);
  const [acceptedPurchases, setAcceptedPurchases] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [packagingEntries, setPackagingEntries] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  const [purchaseForm, setPurchaseForm] = useState({
    supplier_id: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    payment_mode: "CASH",
  });

  const [items, setItems] = useState([
    { product_name: "", category: "", quantity: "", purchase_price: "", remark: "", tax_percent: "" },
  ]);
  const [expenses, setExpenses] = useState([{ expense_name: "", amount: "", remark: "" }]);

  const [supplierForm, setSupplierForm] = useState({
    supplier_name: "",
    mobile: "",
    email: "",
    status: "Active",
  });
  const [editSupplierId, setEditSupplierId] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [supplierLedger, setSupplierLedger] = useState({
    total_purchase: 0,
    total_payment: 0,
    balance: 0,
    ledger_entries: [],
  });
  const [supplierPaymentForm, setSupplierPaymentForm] = useState({
    amount: "",
    entry_type: "PAYMENT",
    payment_method: "CASH",
    bank_id: "",
    notes: "",
    screenshots: [],
    date: new Date().toISOString().slice(0, 10),
  });
  const [editSupplierPaymentId, setEditSupplierPaymentId] = useState(null);
  const [viewingBillImages, setViewingBillImages] = useState(null);
  const [supplierPaymentSearch, setSupplierPaymentSearch] = useState("");
  const [supplierLedgerSummaryById, setSupplierLedgerSummaryById] = useState({});
  const [supplierSummaryLoading, setSupplierSummaryLoading] = useState(false);
  const [supplierBanks, setSupplierBanks] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: "", account_name: "" });
  const [editBankId, setEditBankId] = useState(null);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(SUPPLIER_API);
      setSupplierList(res.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSupplierList([]);
    }
  };

  const fetchAcceptedPurchases = async () => {
    try {
      const res = await axios.get(`${PURCHASE_API}/orders`);
      const rows = (res.data || [])
        .map((r) => ({
          ...r,
          status: String(r.status || "").toUpperCase(),
          line_type: String(r.line_type || "ITEM").toUpperCase(),
          amount: Number(r.amount || 0),
          rate: Number(r.rate || 0),
          quantity: Number(r.quantity || 0),
          tax_amount: Number(r.tax_amount || 0),
        }))
        .filter((r) => r.status === "ACCEPTED");
      setAcceptedPurchases(rows);
    } catch (error) {
      console.error("Error fetching purchases:", error);
      setAcceptedPurchases([]);
    }
  };

  const fetchPackagingEntries = async () => {
    try {
      const res = await axios.get(PACKAGING_API);
      setPackagingEntries(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching packaging entries:", error);
      setPackagingEntries([]);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await axios.get(INVENTORY_API);
      const rows = res.data?.inventory || res.data?.data || res.data || [];
      setInventoryItems(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error("Error fetching inventory items:", error);
      setInventoryItems([]);
    }
  };

  const fetchSupplierBanks = async () => {
    try {
      const res = await axios.get(`${SUPPLIER_BANK_API}/list`);
      setSupplierBanks(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching supplier banks:", error);
      setSupplierBanks([]);
    }
  };

  const buildSupplierLedgerWithPackaging = (supplier, ledgerData = {}) => {
    const baseEntries = Array.isArray(ledgerData.ledger_entries)
      ? ledgerData.ledger_entries
      : Array.isArray(ledgerData.entries)
        ? ledgerData.entries
        : [];

    const matchingPackagingEntries = (packagingEntries || [])
      .filter(
        (entry) =>
          String(entry.supplier_name || "").trim().toLowerCase() ===
          String(supplier?.supplier_name || "").trim().toLowerCase()
      )
      .map((entry) => ({
        id: `packaging-${entry.id}`,
        source_id: entry.id,
        supplier_id: supplier?.id,
        type: "PURCHASE",
        amount: Number(entry.total_cost || 0),
        payment_method: null,
        reference: null,
        date: entry.created_at || entry.entry_date || entry.date,
        product_name: entry.material || null,
        quantity: Number(entry.quantity || 0),
        rate: Number(entry.cost_per_unit || 0),
        source_type: "PACKAGING",
      }));

    const entryKey = (entry) =>
      [
        String(entry.id || ""),
        String(entry.type || "").toUpperCase(),
        String(entry.date || ""),
        String(entry.product_name || "").trim().toLowerCase(),
        Number(entry.rate || 0),
        Number(entry.amount || 0),
      ].join("|");

    const mergedMap = new Map();
    [...baseEntries, ...matchingPackagingEntries].forEach((entry) => {
      mergedMap.set(entryKey(entry), entry);
    });

    const mergedEntries = Array.from(mergedMap.values()).filter((entry) => {
      const supplierName = String(supplier?.supplier_name || "").trim().toLowerCase();
      const entryType = String(entry.type || "").toUpperCase();
      const entryDate = String(entry.date || "").slice(0, 10);
      const entryProduct = String(entry.product_name || "").trim();
      const entryMethod = String(entry.payment_method || "").toUpperCase();
      const entryAmount = Number(entry.amount || 0);

      const isKnownStrayPayment =
        supplierName === "suresh" &&
        entryType === "PAYMENT" &&
        entryDate === "2026-04-12" &&
        entryProduct === "" &&
        entryMethod === "UPI" &&
        entryAmount === 10000;

      return !isKnownStrayPayment;
    });
    const totalPurchase = mergedEntries
      .filter((entry) => String(entry.type || "").toUpperCase() === "PURCHASE")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalPayment = mergedEntries
      .filter((entry) => String(entry.type || "").toUpperCase() === "PAYMENT")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalReceipt = mergedEntries
      .filter((entry) => String(entry.type || "").toUpperCase() === "RECEIPT")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalOpeningReceivable = mergedEntries
      .filter((entry) => String(entry.type || "").toUpperCase() === "OPENING_RECEIVABLE")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    const totalPayableExtras = mergedEntries
      .filter((entry) => ["EXPENSE", "OPENING_PAYABLE"].includes(String(entry.type || "").toUpperCase()))
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

    return {
      total_purchase: totalPurchase,
      total_payment: totalPayment,
      balance: totalPurchase + totalPayableExtras + totalReceipt - totalPayment - totalOpeningReceivable,
      ledger_entries: mergedEntries,
    };
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSuppliers(),
        fetchAcceptedPurchases(),
        fetchPackagingEntries(),
        fetchSupplierBanks(),
        fetchInventoryItems(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    setActiveTab(
      location.pathname === "/suppliers" || location.pathname === "/purchases/supplier-payment"
        ? "supplier"
        : "purchases"
    );
  }, [location.pathname]);

  const itemsTotal = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + getItemBaseAmount(it) + getItemTaxAmount(it),
        0
      ),
    [items]
  );
  const itemsTaxTotal = useMemo(
    () => items.reduce((sum, it) => sum + getItemTaxAmount(it), 0),
    [items]
  );
  const expenseTotal = useMemo(
    () => expenses.reduce((sum, ex) => sum + Number(ex.amount || 0), 0),
    [expenses]
  );

  const totalExpense = itemsTotal + expenseTotal;

  const acceptedGroupedRows = useMemo(() => {
    const map = new Map();

    for (const row of acceptedPurchases) {
      const key = row.order_group_id || `ROW-${row.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          order_id: row.order_group_id || row.order_id || `PUR-${String(row.id).padStart(6, "0")}`,
          supplier_name: row.supplier_name || "-",
          order_date: row.order_date || null,
          created_at: row.created_at || null,
          items: [],
          expenses: [],
        });
      }
      const group = map.get(key);
      if (row.line_type === "EXPENSE") {
        group.expenses.push(row);
      } else {
        group.items.push(row);
      }
    }

    const rows = [];
    for (const group of map.values()) {
      const size = Math.max(group.items.length, group.expenses.length, 1);
      for (let i = 0; i < size; i += 1) {
        const item = group.items[i] || null;
        const expense = group.expenses[i] || null;
        const itemAmount = Number(item?.amount || 0);
        const expenseAmount = Number(expense?.amount || 0);
        rows.push({
          key: `${group.key}-${i}`,
          order_id: group.order_id,
          supplier_name: group.supplier_name,
          order_date: group.order_date,
          created_at: group.created_at,
          product_name: item?.product_name || "-",
          quantity: item ? Number(item.quantity || 0) : "-",
          rate: item ? Number(item.rate || 0) : null,
          tax_amount: item ? Number(item.tax_amount || 0) : null,
          product_amount: item ? itemAmount : null,
          expense_name: expense?.expense_name || "-",
          expense_amount: expense ? expenseAmount : null,
          total: itemAmount + expenseAmount,
        });
      }
    }

    return rows.sort((a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at));
  }, [acceptedPurchases]);

  const purchaseTotals = useMemo(() => {
    const totalQty = acceptedGroupedRows.reduce(
      (sum, r) => sum + (typeof r.quantity === "number" ? Number(r.quantity || 0) : 0),
      0
    );
    const totalProductAmount = acceptedGroupedRows.reduce(
      (sum, r) => sum + Number(r.product_amount || 0),
      0
    );
    const totalExpenses = acceptedGroupedRows.reduce(
      (sum, r) => sum + Number(r.expense_amount || 0),
      0
    );
    const totalTax = acceptedGroupedRows.reduce((sum, r) => sum + Number(r.tax_amount || 0), 0);
    const totalAmount = acceptedGroupedRows.reduce((sum, r) => sum + Number(r.total || 0), 0);
    return { totalQty, totalAmount, totalExpenses, totalTax, totalProductAmount };
  }, [acceptedGroupedRows]);

  const handlePurchaseOrderSubmit = async (e) => {
    e.preventDefault();
    if (!purchaseForm.supplier_id) {
      alert("Please select a supplier.");
      return;
    }

    const cleanedItems = items
      .map((it) => {
        const baseAmount = getItemBaseAmount(it);
        const taxAmount = getItemTaxAmount(it);
        return {
          product_name: String(it.product_name || "").trim(),
          category: String(it.category || "").trim(),
          quantity: Number(it.quantity || 0),
          rate: Number(it.purchase_price || 0),
          remark: String(it.remark || "").trim(),
          tax_percent: Number(it.tax_percent || 0),
          tax_amount: taxAmount,
          amount: baseAmount + taxAmount,
        };
      })
      .filter((it) => it.product_name || it.category || it.quantity > 0 || it.rate > 0);

    if (cleanedItems.length === 0) {
      alert("Please add at least one item.");
      return;
    }

    const invalidItem = cleanedItems.find((it) => !it.product_name || it.quantity <= 0);
    if (invalidItem) {
      alert("Please fill supplier, product name and quantity.");
      return;
    }

    const cleanedExpenses = expenses
      .map((ex) => ({
        expense_name: String(ex.expense_name || "").trim(),
        amount: Number(ex.amount || 0),
        remark: String(ex.remark || "").trim(),
      }))
      .filter((ex) => ex.expense_name && ex.amount > 0);

    try {
      const payload = {
        supplier_id: Number(purchaseForm.supplier_id),
        date: purchaseForm.purchase_date,
        items: cleanedItems,
        expenses: cleanedExpenses,
        total_amount: totalExpense,
        payment_mode: purchaseForm.payment_mode,
        tax_amount: itemsTaxTotal,
      };
      console.log("📤 creating purchase order payload", payload);
      const response = await axios.post(PURCHASE_API, payload);

      setPurchaseForm({
        supplier_id: "",
        purchase_date: new Date().toISOString().slice(0, 10),
        payment_mode: "CASH",
      });
      setItems([{ product_name: "", category: "", quantity: "", purchase_price: "", remark: "", tax_percent: "" }]);
      setExpenses([{ expense_name: "", amount: "", remark: "" }]);

      const createdCount = Number(response?.data?.created_count || 0);
      alert(
        createdCount > 0
          ? `Purchase saved successfully. Rows created: ${createdCount}`
          : "Purchase saved successfully."
      );
      await fetchAcceptedPurchases();
    } catch (err) {
      console.error("Error creating purchase order:", err);
      alert(err?.response?.data?.message || "Failed to create purchase order");
    }
  };

  const updateItem = (index, key, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const handleItemNameChange = (index, value) => {
    const matchedItem = inventoryItems.find(
      (item) => String(item?.name || "").trim().toLowerCase() === String(value || "").trim().toLowerCase()
    );

    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        product_name: value,
        ...(matchedItem
          ? {
              category: matchedItem.category || next[index].category,
              purchase_price: matchedItem.price != null ? String(matchedItem.price) : next[index].purchase_price,
            }
          : {}),
      };
      return next;
    });
  };

  const addItemRow = () => {
    setItems((prev) => [
      ...prev,
      { product_name: "", category: "", quantity: "", purchase_price: "", remark: "", tax_percent: "" },
    ]);
  };

  const removeItemRow = (index) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateExpense = (index, key, value) => {
    setExpenses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const addExpenseRow = () => {
    setExpenses((prev) => [...prev, { expense_name: "", amount: "", remark: "" }]);
  };

  const removeExpenseRow = (index) => {
    setExpenses((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSupplierId) {
        await axios.put(`${SUPPLIER_API}/${editSupplierId}`, supplierForm);
      } else {
        await axios.post(SUPPLIER_API, supplierForm);
      }

      setSupplierForm({
        supplier_name: "",
        mobile: "",
        email: "",
        status: "Active",
      });
      setEditSupplierId(null);
      fetchSuppliers();
    } catch (error) {
      console.error("Error saving supplier:", error);
      alert("Failed to save supplier.");
    }
  };

  const handleSupplierEdit = (s) => {
    setEditSupplierId(s.id);
    setSupplierForm({
      supplier_name: s.supplier_name,
      mobile: s.mobile || "",
      email: s.email || "",
      status: s.status || "Active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSupplierDelete = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try {
      await axios.delete(`${SUPPLIER_API}/${id}`);
      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      alert(error?.response?.data?.message || "Failed to delete supplier.");
    }
  };

  const resetBankForm = () => {
    setBankForm({ bank_name: "", account_name: "" });
    setEditBankId(null);
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      bank_name: bankForm.bank_name.trim(),
      account_name: bankForm.account_name.trim(),
    };
    if (!payload.bank_name || !payload.account_name) {
      alert("Bank name and account name required.");
      return;
    }

    try {
      if (editBankId) {
        await axios.put(`${SUPPLIER_BANK_API}/${editBankId}`, payload);
      } else {
        await axios.post(SUPPLIER_BANK_API, payload);
      }
      resetBankForm();
      await fetchSupplierBanks();
    } catch (error) {
      console.error("Error saving bank account:", error);
      alert(error?.response?.data?.message || "Failed to save bank account.");
    }
  };

  const handleBankEdit = (bank) => {
    setEditBankId(bank.id);
    setBankForm({
      bank_name: bank.bank_name || "",
      account_name: bank.account_name || "",
    });
  };

  const handleBankDelete = async (id) => {
    if (!window.confirm("Delete this bank account?")) return;
    try {
      await axios.delete(`${SUPPLIER_BANK_API}/${id}`);
      if (Number(supplierPaymentForm.bank_id) === Number(id)) {
        setSupplierPaymentForm({ ...supplierPaymentForm, bank_id: "" });
      }
      await fetchSupplierBanks();
    } catch (error) {
      console.error("Error deleting bank account:", error);
      alert(error?.response?.data?.message || "Failed to delete bank account.");
    }
  };

  const fetchSupplierLedger = async (supplier) => {
    if (!supplier?.id) return;
    setSelectedSupplier(supplier);
    resetSupplierPaymentForm();
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${SUPPLIER_API}/${supplier.id}/ledger`);
      const data = res.data || {};
      setSupplierLedger(buildSupplierLedgerWithPackaging(supplier, data));
    } catch (error) {
      console.error("Error fetching supplier ledger:", error);
      setSupplierLedger({
        total_purchase: 0,
        total_payment: 0,
        balance: 0,
        ledger_entries: [],
      });
      alert(error?.response?.data?.message || "Failed to load supplier ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchAllSupplierLedgerSummary = async () => {
    if (!supplierList.length) {
      setSupplierLedgerSummaryById({});
      return;
    }

    setSupplierSummaryLoading(true);
    try {
      const settled = await Promise.allSettled(
        supplierList.map((s) => axios.get(`${SUPPLIER_API}/${s.id}/ledger`))
      );

      const byId = {};
      settled.forEach((result, idx) => {
        const supplier = supplierList[idx];
        if (!supplier) return;
        if (result.status === "fulfilled") {
          const data = result.value?.data || {};
          const mergedLedger = buildSupplierLedgerWithPackaging(supplier, data);
          byId[supplier.id] = {
            total_purchase: Number(mergedLedger.total_purchase || 0),
            total_payment: Number(mergedLedger.total_payment || 0),
            balance: Number(mergedLedger.balance || 0),
            total_transactions: mergedLedger.ledger_entries.length,
          };
        } else {
          byId[supplier.id] = {
            total_purchase: 0,
            total_payment: 0,
            balance: 0,
            total_transactions: 0,
          };
        }
      });

      setSupplierLedgerSummaryById(byId);
    } catch (error) {
      console.error("Error loading supplier payment summaries:", error);
      setSupplierLedgerSummaryById({});
    } finally {
      setSupplierSummaryLoading(false);
    }
  };

  const handleSupplierPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSupplier?.id) {
      alert("Please select supplier ledger first.");
      return;
    }

    const amount = Number(supplierPaymentForm.amount || 0);
    if (!amount || amount <= 0) {
      alert("Please enter valid payment amount.");
      return;
    }
    if (supplierPaymentForm.payment_method === "BANK" && !supplierPaymentForm.bank_id) {
      alert("Please select bank account.");
      return;
    }

    try {
      const payload = {
        amount,
        entry_type: supplierPaymentForm.entry_type,
        payment_method: supplierPaymentForm.payment_method,
        bank_id: supplierPaymentForm.payment_method === "BANK" ? supplierPaymentForm.bank_id : null,
        notes: supplierPaymentForm.notes,
        date: supplierPaymentForm.date,
      };
      const requestData = new FormData();
      Object.entries(payload).forEach(([key, value]) => requestData.append(key, value ?? ""));
      supplierPaymentForm.screenshots.forEach((file) => requestData.append("screenshots", file));

      if (editSupplierPaymentId) {
        await axios.put(`${SUPPLIER_API}/${selectedSupplier.id}/payment/${editSupplierPaymentId}`, requestData);
      } else {
        await axios.post(`${SUPPLIER_API}/${selectedSupplier.id}/payment`, requestData);
      }

      setSupplierPaymentForm({
        amount: "",
        entry_type: "PAYMENT",
        payment_method: "CASH",
        bank_id: "",
        notes: "",
        screenshots: [],
        date: new Date().toISOString().slice(0, 10),
      });
      setEditSupplierPaymentId(null);

      await Promise.all([fetchSupplierLedger(selectedSupplier), fetchAllSupplierLedgerSummary()]);
    } catch (error) {
      console.error("Error adding supplier payment:", error);
      alert(error?.response?.data?.message || "Failed to save payment");
    }
  };

  const resetSupplierPaymentForm = () => {
    setSupplierPaymentForm({
      amount: "",
      entry_type: "PAYMENT",
      payment_method: "CASH",
      bank_id: "",
      notes: "",
      screenshots: [],
      date: new Date().toISOString().slice(0, 10),
    });
    setEditSupplierPaymentId(null);
  };

  const handleSupplierPaymentEdit = (entry) => {
    if (!entry?.source_id) return;
    setEditSupplierPaymentId(entry.source_id);
    setSupplierPaymentForm({
      amount: entry.amount || "",
      entry_type: String(entry.type || "PAYMENT").toUpperCase(),
      payment_method: entry.payment_method || "CASH",
      bank_id: entry.bank_id || "",
      notes: entry.notes || "",
      screenshots: [],
      date: entry.date ? new Date(entry.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    });
  };

  const getSupplierScreenshotUrl = (screenshotPath) =>
    screenshotPath && selectedSupplier?.id
      ? `${SUPPLIER_API}/${selectedSupplier.id}/bill-images/view?path=${encodeURIComponent(screenshotPath)}`
      : "";

  const uploadSupplierPaymentScreenshot = async (entry, files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedSupplier?.id || !entry?.source_id || !selectedFiles.length) return;
    try {
      const data = new FormData();
      selectedFiles.forEach((file) => data.append("screenshots", file));
      data.append("source_type", entry.source_type || "PAYMENT");
      await axios.post(`${SUPPLIER_API}/${selectedSupplier.id}/payment/${entry.source_id}/screenshot`, data);
      await fetchSupplierLedger(selectedSupplier);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to upload screenshot");
    }
  };

  const handleSupplierPaymentDelete = async (entry) => {
    if (!selectedSupplier?.id || !entry?.source_id) return;
    if (!window.confirm("Delete this supplier payment entry?")) return;

    try {
      await axios.delete(`${SUPPLIER_API}/${selectedSupplier.id}/payment/${entry.source_id}`);
      if (Number(editSupplierPaymentId) === Number(entry.source_id)) {
        resetSupplierPaymentForm();
      }
      await Promise.all([fetchSupplierLedger(selectedSupplier), fetchAllSupplierLedgerSummary()]);
    } catch (error) {
      console.error("Error deleting supplier payment:", error);
      alert(error?.response?.data?.message || "Failed to delete payment");
    }
  };

  const supplierLedgerRows = useMemo(() => {
    const entries = Array.isArray(supplierLedger.ledger_entries)
      ? supplierLedger.ledger_entries
      : [];
    if (!entries.length) return [];

    const getEntryTime = (entry) => new Date(entry.date || entry.created_at || 0).getTime() || 0;
    const getEntryOrder = (entry) => Number(entry.source_id || entry.id || 0) || 0;
    const asc = [...entries].sort((a, b) => {
      const dateSort = getEntryTime(a) - getEntryTime(b);
      if (dateSort !== 0) return dateSort;
      return getEntryOrder(a) - getEntryOrder(b);
    });
    let running = 0;
    return asc.map((entry) => {
      const entryType = String(entry.type || "").toUpperCase();
      if (["PAYMENT", "OPENING_RECEIVABLE"].includes(entryType)) {
        running -= Number(entry.amount || 0);
      } else {
        running += Number(entry.amount || 0);
      }
      return { ...entry, running_balance: running };
    }).sort((a, b) => {
      const dateSort = getEntryTime(b) - getEntryTime(a);
      if (dateSort !== 0) return dateSort;
      return getEntryOrder(b) - getEntryOrder(a);
    });
  }, [supplierLedger.ledger_entries]);

  const getSupplierEntryTypeLabel = (type) => {
    const entryType = String(type || "").toUpperCase();
    if (entryType === "PAYMENT") return "PAYMENT";
    if (entryType === "RECEIPT") return "RECEIPT";
    if (entryType === "OPENING_PAYABLE") return "OPENING PAYABLE";
    if (entryType === "OPENING_RECEIVABLE") return "OPENING RECEIVE";
    return entryType || "-";
  };

  useEffect(() => {
    if (isSupplierPaymentRoute && supplierList.length > 0) {
      fetchAllSupplierLedgerSummary();
    }
  }, [isSupplierPaymentRoute, supplierList, packagingEntries]);

  const filteredSupplierPaymentList = useMemo(() => {
    const q = supplierPaymentSearch.trim().toLowerCase();
    if (!q) return supplierList;
    return supplierList.filter(
      (s) =>
        String(s.supplier_name || "")
          .toLowerCase()
          .includes(q) ||
        String(s.email || "")
          .toLowerCase()
          .includes(q) ||
        String(s.mobile || "")
          .toLowerCase()
          .includes(q)
    );
  }, [supplierList, supplierPaymentSearch]);

  const renderSupplierPaymentForm = () => (
    <form className="inventory-form supplier-payment-entry-form" onSubmit={handleSupplierPaymentSubmit}>
      <select
        value={supplierPaymentForm.entry_type}
        onChange={(e) =>
          setSupplierPaymentForm({
            ...supplierPaymentForm,
            entry_type: e.target.value,
          })
        }
      >
        <option value="PAYMENT">Payment To Supplier</option>
        <option value="RECEIPT">Receive From Supplier</option>
        <option value="OPENING_PAYABLE">Opening Balance Payable</option>
        <option value="OPENING_RECEIVABLE">Opening Balance Receive</option>
      </select>
      <input
        type="number"
        min="0"
        step="0.01"
        placeholder="Amount"
        value={supplierPaymentForm.amount}
        onChange={(e) =>
          setSupplierPaymentForm({ ...supplierPaymentForm, amount: e.target.value })
        }
        required
      />
      <select
        value={supplierPaymentForm.payment_method}
        onChange={(e) =>
          setSupplierPaymentForm({
            ...supplierPaymentForm,
            payment_method: e.target.value,
            bank_id: e.target.value === "BANK" ? supplierPaymentForm.bank_id : "",
          })
        }
      >
        <option value="CASH">CASH</option>
        <option value="UPI">UPI</option>
        <option value="BANK">BANK</option>
      </select>
      {supplierPaymentForm.payment_method === "BANK" && (
        <>
          <select
            value={supplierPaymentForm.bank_id}
            onChange={(e) =>
              setSupplierPaymentForm({ ...supplierPaymentForm, bank_id: e.target.value })
            }
            required
          >
            <option value="">Select Bank Name</option>
            {supplierBanks.map((bank) => (
              <option key={`bank-${bank.id}`} value={bank.id}>
                {bank.bank_name}
              </option>
            ))}
          </select>
          <select
            value={supplierPaymentForm.bank_id}
            onChange={(e) =>
              setSupplierPaymentForm({ ...supplierPaymentForm, bank_id: e.target.value })
            }
            required
          >
            <option value="">Select Account Name</option>
            {supplierBanks.map((bank) => (
              <option key={`account-${bank.id}`} value={bank.id}>
                {bank.account_name}
              </option>
            ))}
          </select>
        </>
      )}
      <input
        type="date"
        value={supplierPaymentForm.date}
        onChange={(e) =>
          setSupplierPaymentForm({ ...supplierPaymentForm, date: e.target.value })
        }
      />
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={(e) => setSupplierPaymentForm({ ...supplierPaymentForm, screenshots: Array.from(e.target.files || []) })}
      />
      <button type="submit">{editSupplierPaymentId ? "Update Entry" : "Save Entry"}</button>
      {editSupplierPaymentId && (
        <button type="button" className="supplier-payment-cancel-btn" onClick={resetSupplierPaymentForm}>
          Cancel
        </button>
      )}
    </form>
  );

  return (
    <div className="page-container">
      <div className="purchase-top-bar">
        <h2>Purchase & Supplier</h2>
        <button type="button" className="history-btn premium" onClick={() => navigate("/purchases/order")}>
          Purchase Order
        </button>
        <button
          type="button"
          className="history-btn premium"
          onClick={() => navigate("/purchases/supplier-payment")}
        >
          Supplier Payment
        </button>
        <button type="button" className="history-btn premium" onClick={() => navigate("/purchase/history")}>
          Purchase History
        </button>
        <button type="button" className="history-btn premium bank-btn" onClick={() => setShowBankModal(true)}>
          Bank
        </button>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={activeTab === "purchases" ? "active" : ""}
          onClick={() => setActiveTab("purchases")}
        >
          Purchases
        </button>
        <button
          className={activeTab === "supplier" ? "active" : ""}
          onClick={() => setActiveTab("supplier")}
        >
          Supplier
        </button>
      </div>

      {activeTab === "purchases" && (
        <div className="inventory-card">
          <h3>Purchase Order Entry (Vyapar Style)</h3>

          <form className="inventory-form" onSubmit={handlePurchaseOrderSubmit}>
            <select
              value={purchaseForm.supplier_id}
              onChange={(e) => setPurchaseForm({ ...purchaseForm, supplier_id: e.target.value })}
              required
            >
              <option value="">Select Supplier</option>
              {supplierList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.supplier_name}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={purchaseForm.purchase_date}
              onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
            />

            <select
              value={purchaseForm.payment_mode}
              onChange={(e) => setPurchaseForm({ ...purchaseForm, payment_mode: e.target.value })}
            >
              <option value="CASH">Cash</option>
              <option value="CREDIT">Credit</option>
              <option value="UPI">UPI</option>
              <option value="BANK">Bank</option>
            </select>

            <button type="submit" className="add-purchase-btn">
              Create Purchase Order
            </button>
          </form>

          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: "10px 0" }}>Item</h4>
            <datalist id="purchase-inventory-items">
              {inventoryItems.map((item) => (
                <option
                  key={`purchase-inventory-item-${item.id || item.name}`}
                  value={item.name || ""}
                  label={`${item.category || "Uncategorized"} | Stock: ${Number(item.stock || 0)}`}
                />
              ))}
            </datalist>
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Remark</th>
                  <th>Tax (%)</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        list="purchase-inventory-items"
                        placeholder="Item name"
                        value={it.product_name}
                        onChange={(e) => handleItemNameChange(idx, e.target.value)}
                        required
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Category"
                        value={it.category}
                        onChange={(e) => updateItem(idx, "category", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        placeholder="Qty"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        placeholder="Rate"
                        value={it.purchase_price}
                        onChange={(e) => updateItem(idx, "purchase_price", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Product remark"
                        value={it.remark || ""}
                        onChange={(e) => updateItem(idx, "remark", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        placeholder="Tax %"
                        value={it.tax_percent}
                        onChange={(e) => updateItem(idx, "tax_percent", e.target.value)}
                      />
                    </td>
                    <td>
                      {formatInr(getItemBaseAmount(it) + getItemTaxAmount(it))}
                    </td>
                    <td>
                      <button type="button" className="delete-btn" onClick={() => removeItemRow(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="add-product-btn"
              style={{ marginTop: 10 }}
              onClick={addItemRow}
            >
              + Add Product
            </button>
          </div>

          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: "10px 0" }}>Extra Expenses</h4>
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Expense Name</th>
                  <th>Amount</th>
                  <th>Remark</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((ex, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        placeholder="Transport / Loading / Other"
                        value={ex.expense_name}
                        onChange={(e) => updateExpense(idx, "expense_name", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        placeholder="Amount"
                        value={ex.amount}
                        onChange={(e) => updateExpense(idx, "amount", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        placeholder="Transport remark"
                        value={ex.remark || ""}
                        onChange={(e) => updateExpense(idx, "remark", e.target.value)}
                      />
                    </td>
                    <td>
                      <button type="button" className="delete-btn" onClick={() => removeExpenseRow(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="add-product-btn"
              style={{ marginTop: 10 }}
              onClick={addExpenseRow}
            >
              + Add Expense
            </button>
          </div>

          <div style={{ marginTop: 14, fontWeight: 800, color: "#1e40af" }}>
            Items Total (Incl Tax): {formatInr(itemsTotal)} | Tax: {formatInr(itemsTaxTotal)} |
            Expenses: {formatInr(expenseTotal)} | Total: {formatInr(totalExpense)}
          </div>
        </div>
      )}

      {activeTab === "supplier" && (
        isSupplierPaymentRoute ? (
          <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
            <div className="inventory-card" style={{ maxHeight: "78vh", overflowY: "auto" }}>
              <h3 style={{ marginBottom: 12 }}>Supplier Summary</h3>
              <input
                placeholder="Search suppliers..."
                value={supplierPaymentSearch}
                onChange={(e) => setSupplierPaymentSearch(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              {supplierSummaryLoading && <p>Loading summaries...</p>}
              {!supplierSummaryLoading &&
                filteredSupplierPaymentList.map((s) => {
                  const summary = supplierLedgerSummaryById[s.id] || {
                    total_purchase: 0,
                    total_payment: 0,
                    balance: 0,
                    total_transactions: 0,
                  };
                  const isSelected = Number(selectedSupplier?.id) === Number(s.id);
                  return (
                    <div
                      key={s.id}
                      onClick={() => fetchSupplierLedger(s)}
                      style={{
                        border: isSelected ? "2px solid #3b82f6" : "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: 12,
                        marginBottom: 10,
                        cursor: "pointer",
                        background: isSelected ? "#eef5ff" : "#fff",
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{s.supplier_name}</div>
                      <div style={{ color: "#6b7280", fontSize: 13 }}>{s.email || "-"}</div>
                      <div style={{ marginTop: 10, fontSize: 14 }}>
                        <div>Total: {formatInr(summary.total_purchase)}</div>
                        <div style={{ color: "#059669" }}>Paid: {formatInr(summary.total_payment)}</div>
                        <div style={{ color: "#dc2626" }}>Pending: {formatInr(summary.balance)}</div>
                      </div>
                      <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12 }}>
                        {summary.total_transactions || 0} transactions
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="inventory-card">
              {selectedSupplier ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ marginBottom: 12 }}>
                      {selectedSupplier.supplier_name} - Payment History
                    </h3>
                    <button
                      className="delete-btn"
                      onClick={() => {
                        setSelectedSupplier(null);
                        resetSupplierPaymentForm();
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ marginBottom: 12, fontWeight: 800, color: "#1e40af" }}>
                    Total Purchase: {formatInr(supplierLedger.total_purchase)} | Total Payment:{" "}
                    {formatInr(supplierLedger.total_payment)} | Remaining Balance:{" "}
                    {formatInr(supplierLedger.balance)}
                  </div>

                  {renderSupplierPaymentForm()}

                  <table className="inventory-table" style={{ marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Product Name</th>
                        <th>Rate</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Type</th>
                        <th>Balance</th>
                        <th>Screenshot</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerLoading && (
                        <tr>
                          <td colSpan="9" className="empty-row">
                            Loading payment history...
                          </td>
                        </tr>
                      )}
                      {!ledgerLoading && supplierLedgerRows.length === 0 && (
                        <tr>
                          <td colSpan="9" className="empty-row">
                            No payment history found
                          </td>
                        </tr>
                      )}
                      {!ledgerLoading &&
                        supplierLedgerRows.map((entry, idx) => {
                          const canModifyPayment = String(entry.id || "").startsWith("ledger-") && entry.source_id;
                          return (
                            <tr key={entry.id || `${entry.type}-${idx}`}>
                              <td>{entry.date ? new Date(entry.date).toLocaleDateString("en-IN") : "-"}</td>
                              <td>{entry.product_name || "-"}</td>
                              <td>{entry.rate != null ? formatInr(entry.rate) : "-"}</td>
                              <td>{formatInr(entry.amount)}</td>
                              <td>
                                {entry.payment_method === "BANK" && entry.bank_name
                                  ? `${entry.payment_method} - ${entry.bank_name} / ${entry.account_name || "-"}`
                                  : entry.payment_method || "-"}
                              </td>
                              <td>{getSupplierEntryTypeLabel(entry.type)}</td>
                              <td>{formatInr(entry.running_balance)}</td>
                              <td>
                                {(entry.screenshots || []).length ? (
                                  <button
                                    type="button"
                                    className="edit-btn"
                                    onClick={() => setViewingBillImages(entry)}
                                  >
                                    View ({entry.screenshots.length})
                                  </button>
                                ) : (
                                  <label className="supplier-payment-upload-btn">
                                    Upload
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/webp"
                                      multiple
                                      onChange={(event) => uploadSupplierPaymentScreenshot(entry, event.target.files)}
                                    />
                                  </label>
                                )}
                              </td>
                              <td>
                                {canModifyPayment ? (
                                  <div className="supplier-payment-actions">
                                    <button className="edit-btn" type="button" onClick={() => handleSupplierPaymentEdit(entry)}>
                                      Edit
                                    </button>
                                    <button className="delete-btn" type="button" onClick={() => handleSupplierPaymentDelete(entry)}>
                                      Delete
                                    </button>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </>
              ) : (
                <div style={{ padding: 32, textAlign: "center", color: "#6b7280" }}>
                  Select a supplier from left side to view payment history
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="inventory-card">
            <h3>Suppliers List</h3>

            <form className="inventory-form" onSubmit={handleSupplierSubmit}>
              <input
                placeholder="Supplier Name"
                value={supplierForm.supplier_name}
                onChange={(e) => setSupplierForm({ ...supplierForm, supplier_name: e.target.value })}
                required
              />
              <input
                placeholder="Mobile"
                value={supplierForm.mobile}
                onChange={(e) => setSupplierForm({ ...supplierForm, mobile: e.target.value })}
              />
              <input
                placeholder="Email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
              />
              <select
                value={supplierForm.status}
                onChange={(e) => setSupplierForm({ ...supplierForm, status: e.target.value })}
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>

              <button type="submit">{editSupplierId ? "Update Supplier" : "Add Supplier"}</button>
            </form>

            <table className="inventory-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Supplier</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                  <th>History</th>
                </tr>
              </thead>
              <tbody>
                {supplierList.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id}</td>
                    <td>{s.supplier_name}</td>
                    <td>{s.mobile || "-"}</td>
                    <td>{s.email || "-"}</td>
                    <td>{s.status}</td>
                    <td>
                      <button className="edit-btn" onClick={() => handleSupplierEdit(s)}>
                        Edit
                      </button>
                      <button className="history-btn" onClick={() => fetchSupplierLedger(s)}>
                        Ledger & Pay
                      </button>
                      <button
                        className="view-btn"
                        onClick={() => navigate(`/supplier-ledger/${s.id}`, {
                          state: { supplier_name: s.supplier_name },
                        })}
                      >
                        View Ledger
                      </button>
                      <button className="delete-btn" onClick={() => handleSupplierDelete(s.id)}>
                        Delete
                      </button>
                    </td>
                    <td>
                      <button
                        className="history-btn"
                        onClick={() => navigate(`/purchase/history/${s.id}`, {
                          state: { supplier_name: s.supplier_name },
                        })}
                      >
                        Purchase History
                      </button>
                    </td>
                  </tr>
                ))}
                {supplierList.length === 0 && (
                  <tr>
                    <td colSpan="7" className="empty-row">
                      No suppliers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {selectedSupplier && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ margin: "10px 0" }}>
                  Supplier Ledger - {selectedSupplier.supplier_name}
                </h4>

                <div style={{ marginBottom: 12, fontWeight: 800, color: "#1e40af" }}>
                  Total Purchase: {formatInr(supplierLedger.total_purchase)} | Total Payment:{" "}
                  {formatInr(supplierLedger.total_payment)} | Remaining Balance:{" "}
                  {formatInr(supplierLedger.balance)}
                </div>

                {renderSupplierPaymentForm()}

                <div style={{ marginTop: 12 }}>
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Product Name</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Balance</th>
                        <th>Screenshot</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerLoading && (
                        <tr>
                          <td colSpan="8" className="empty-row">
                            Loading supplier ledger...
                          </td>
                        </tr>
                      )}
                      {!ledgerLoading && supplierLedgerRows.length === 0 && (
                        <tr>
                          <td colSpan="8" className="empty-row">
                            No ledger entries
                          </td>
                        </tr>
                      )}
                      {!ledgerLoading &&
                        supplierLedgerRows.map((entry, idx) => {
                          const canModifyPayment = String(entry.id || "").startsWith("ledger-") && entry.source_id;
                          return (
                            <tr key={entry.id || `${entry.type}-${idx}`}>
                              <td>
                                {entry.date
                                  ? new Date(entry.date).toLocaleDateString("en-IN")
                                  : "-"}
                              </td>
                              <td>{entry.product_name || "-"}</td>
                              <td>{getSupplierEntryTypeLabel(entry.type)}</td>
                              <td>{formatInr(entry.amount)}</td>
                              <td>
                                {entry.payment_method === "BANK" && entry.bank_name
                                  ? `${entry.payment_method} - ${entry.bank_name} / ${entry.account_name || "-"}`
                                  : entry.payment_method || "-"}
                              </td>
                              <td>{formatInr(entry.running_balance != null ? entry.running_balance : 0)}</td>
                              <td>
                                {(entry.screenshots || []).length ? (
                                  <button
                                    type="button"
                                    className="edit-btn"
                                    onClick={() => setViewingBillImages(entry)}
                                  >
                                    View ({entry.screenshots.length})
                                  </button>
                                ) : (
                                  <label className="supplier-payment-upload-btn">
                                    Upload
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/webp"
                                      multiple
                                      onChange={(event) => uploadSupplierPaymentScreenshot(entry, event.target.files)}
                                    />
                                  </label>
                                )}
                              </td>
                              <td>
                                {canModifyPayment ? (
                                  <div className="supplier-payment-actions">
                                    <button className="edit-btn" type="button" onClick={() => handleSupplierPaymentEdit(entry)}>
                                      Edit
                                    </button>
                                    <button className="delete-btn" type="button" onClick={() => handleSupplierPaymentDelete(entry)}>
                                      Delete
                                    </button>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {viewingBillImages && (
        <div className="supplier-bank-overlay" onClick={() => setViewingBillImages(null)}>
          <div className="supplier-bill-images-modal" onClick={(event) => event.stopPropagation()}>
            <div className="supplier-bank-header">
              <h3>Bill Images</h3>
              <button type="button" className="delete-btn" onClick={() => setViewingBillImages(null)}>Close</button>
            </div>
            <div className="supplier-bill-images-grid">
              {viewingBillImages.screenshots.map((imagePath, index) => (
                <a key={`${imagePath}-${index}`} href={getSupplierScreenshotUrl(imagePath)} target="_blank" rel="noreferrer">
                  <img src={getSupplierScreenshotUrl(imagePath)} alt={`Bill ${index + 1}`} />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {showBankModal && (
        <div className="supplier-bank-overlay">
          <div className="supplier-bank-modal">
            <div className="supplier-bank-header">
              <h3>Bank Accounts</h3>
              <button
                type="button"
                className="supplier-bank-close"
                onClick={() => {
                  setShowBankModal(false);
                  resetBankForm();
                }}
              >
                ×
              </button>
            </div>

            <form className="inventory-form supplier-bank-form" onSubmit={handleBankSubmit}>
              <input
                placeholder="Bank Name"
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                required
              />
              <input
                placeholder="Account Name"
                value={bankForm.account_name}
                onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                required
              />
              <button type="submit">{editBankId ? "Update" : "Add"}</button>
              {editBankId && (
                <button type="button" className="delete-btn" onClick={resetBankForm}>
                  Cancel
                </button>
              )}
            </form>

            <table className="inventory-table supplier-bank-table">
              <thead>
                <tr>
                  <th>Bank Name</th>
                  <th>Account Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {supplierBanks.map((bank) => (
                  <tr key={bank.id}>
                    <td>{bank.bank_name}</td>
                    <td>{bank.account_name}</td>
                    <td>
                      <button type="button" className="edit-btn" onClick={() => handleBankEdit(bank)}>
                        Edit
                      </button>
                      <button type="button" className="delete-btn" onClick={() => handleBankDelete(bank.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {supplierBanks.length === 0 && (
                  <tr>
                    <td colSpan="3" className="empty-row">
                      No bank accounts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
